import { GetStaticProps } from "next";
import { useRouter } from "next/router";

import { BodyClass } from "@/helpers/BodyClass";
import { ISeoImage, seoImage as getSeoImage } from "@/meta/seoImage";
import { SEO } from "@/meta/seo";
import {
  getAllPosts,
  getAllSettings,
  GhostPostOrPage,
  GhostPostsOrPages,
  GhostSettings,
} from "@/lib/ghost";
import { processEnv } from "@/lib/processEnv";

import { Layout } from "@/components/Blog/Layout";
import { PostView } from "@/components/Blog/PostView";
import LandingHeader from "@/components/LandingHeader";

interface CmsData {
  posts: GhostPostsOrPages;
  settings: GhostSettings;
  seoImage: ISeoImage;
  previewPosts?: GhostPostsOrPages;
  prevPost?: GhostPostOrPage;
  nextPost?: GhostPostOrPage;
  bodyClass: string;
}

interface IndexProps {
  cmsData: CmsData;
}

export default function Index({ cmsData }: IndexProps) {
  const router = useRouter();
  if (router.isFallback) return <div>Loading...</div>;

  const { settings, posts, seoImage, bodyClass } = cmsData;

  return (
    <>
      <SEO {...{ settings, seoImage }} />
      <Layout
        {...{ bodyClass, settings, isHome: true }}
        header={<LandingHeader />}
      >
        <PostView {...{ settings, posts, isHome: true }} />
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  let settings;
  let posts: GhostPostsOrPages | [];

  console.time("Index - getStaticProps");

  try {
    settings = await getAllSettings();
    posts = await getAllPosts();
  } catch (error) {
    throw new Error("Index creation failed.");
  }

  const cmsData = {
    settings,
    posts,
    seoImage: await getSeoImage({ siteUrl: settings.processEnv.siteUrl }),
    bodyClass: BodyClass({ isHome: true }),
  };

  console.timeEnd("Index - getStaticProps");

  return {
    props: {
      cmsData,
    },
    ...(processEnv.isr.enable && { revalidate: processEnv.isr.revalidate }), // re-generate at most once every revalidate second
  };
};
